import { queryOptions } from "@tanstack/react-query";
import { STATES } from "@/data/states";
import { ALL_GAMES, type Game } from "@/data/games";
import { getLatestResults, generateLatestResultForGame } from "@/data/results";
import { fetchLiveLottery, buildLiveMaps } from "@/lib/lottery-api.functions";
import { getGameOverridesCached } from "@/lib/game-overrides-read.functions";

type GameOverrideRow = {
  slug: string;
  name: string | null;
  logo_url: string | null;
  draw_schedule: string | null;
  description: string | null;
  updated_at?: string | null;
};

function applyOverride(g: Game, o?: GameOverrideRow | null): Game {
  if (!o) return g;
  return {
    ...g,
    name: o.name ?? g.name,
    logo_url: o.logo_url ?? g.logo_url,
    draw_schedule: o.draw_schedule ?? g.draw_schedule,
    description: o.description ?? g.description,
  };
}

function attachStates(g: Game) {
  if (g.is_multistate) return { ...g, states: null };
  const st = STATES.find((s) => s.id === g.state_id);
  return {
    ...g,
    states: st
      ? { name: st.name, slug: st.slug, abbreviation: st.abbreviation, official_lottery_url: st.official_lottery_url }
      : null,
  };
}

async function fetchOverrides(): Promise<GameOverrideRow[]> {
  try {
    return (await getGameOverridesCached()) as GameOverrideRow[];
  } catch {
    return [];
  }
}

export type LatestResultDTO = {
  id: string;
  game_id: string;
  draw_date: string;
  winning_numbers: number[];
  bonus_numbers: number[];
  multiplier: number | null;
  games: (Game & { states: { name: string; slug: string; abbreviation: string; official_lottery_url: string | null } | null }) | undefined;
};

async function computeLatestResults(limit: number): Promise<LatestResultDTO[]> {
  const [live, overrides] = await Promise.all([fetchLiveLottery(), fetchOverrides()]);
  const maps = buildLiveMaps(live);
  const overrideMap = new Map<string, GameOverrideRow>(overrides.map((o) => [o.slug, o]));

  const base = getLatestResults(limit * 2).map((r) => {
    const live = maps.latestByGameId[r.game_id];
    if (!live) return r;
    if (r.draw_date > live.draw_date) return r; // live feed wins on equal dates so real winning numbers show
    return {
      ...r,
      draw_date: live.draw_date,
      winning_numbers: live.winning_numbers,
      bonus_numbers: live.bonus_numbers,
      multiplier: live.multiplier,
    };
  });

  const seen = new Set(base.map((r) => r.game_id));
  const synth = maps.syntheticGames
    .filter((s) => !seen.has(s.game.id))
    .map((s) => {
      if (s.latest) {
        return {
          id: `r-${s.game.id}-${s.latest.draw_date}`,
          game_id: s.game.id,
          draw_date: s.latest.draw_date,
          winning_numbers: s.latest.winning_numbers,
          bonus_numbers: s.latest.bonus_numbers,
          multiplier: s.latest.multiplier,
          games: applyOverride(s.game, overrideMap.get(s.game.slug)),
        };
      }
      const gen = generateLatestResultForGame(s.game);
      if (!gen) return null;
      return { ...gen, games: applyOverride(s.game, overrideMap.get(s.game.slug)) };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  return [...base, ...synth]
    .sort((a, b) => b.draw_date.localeCompare(a.draw_date))
    .slice(0, limit)
    .map((r) => {
      const game = r.games as Game;
      const withOverride = applyOverride(game, overrideMap.get(game.slug));
      return {
        id: r.id,
        game_id: r.game_id,
        draw_date: r.draw_date,
        winning_numbers: r.winning_numbers,
        bonus_numbers: r.bonus_numbers,
        multiplier: r.multiplier,
        games: attachStates(withOverride) as LatestResultDTO["games"],
      };
    });
}

/**
 * Cached per limit: the result set is identical for every visitor, so under
 * traffic the merge + live-feed reconciliation runs once a minute per worker.
 */
async function computeLatestResultsCached(limit: number): Promise<LatestResultDTO[]> {
  const { cached } = await import("@/lib/server-cache");
  return cached(`ssr:latest-results:${limit}`, 60_000, () => computeLatestResults(limit), {
    staleWhileRevalidateMs: 10 * 60_000,
  });
}

export const latestResultsQueryOptions = (limit = 15) =>
  queryOptions({
    queryKey: ["latest-results-ssr", limit] as const,
    queryFn: () => computeLatestResultsCached(limit),
    staleTime: 5 * 60 * 1000,
  });